import { useQuery } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

/**
 * Fetches the permission bundle for a single entity (canRead, canUpdate,
 * canDelete, canInsertChild). Called once per entity detail page and cached
 * by entityId.
 *
 * Only active when the `trpc` feature flag is enabled.
 */
export const useEntityPermissions = (entityId: string | undefined) => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();

  const { data, isPending } = useQuery({
    ...trpc.frontend.permission.getEntityPermissions.queryOptions({
      entityId: entityId!,
    }),
    enabled: trpcEnabled && !!entityId,
    gcTime: 30 * 60 * 1000,
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchInterval: false,
  });

  return {
    canRead: data?.canRead ?? false,
    canUpdate: data?.canUpdate ?? false,
    canDelete: data?.canDelete ?? false,
    canInsertChild: data?.canInsertChild ?? false,
    // `isPending` (no data yet) rather than `isLoading` (`isPending &&
    // isFetching`): an enabled-but-idle query has `isLoading === false` while
    // `data` is still undefined, which would briefly report "loaded, denied"
    // and flash gated UI. `isPending` stays true until data lands.
    isLoading: trpcEnabled ? isPending : true,
  };
};
